import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Paperclip, Send, X } from 'lucide-react';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { useToast } from '@/shared/hooks/useToast';
import { cn } from '@/shared/utils/cn';

const MAX_FILE_BYTES = 10 * 1024 * 1024;

type Props = {
  disabled?: boolean;
  sending?: boolean;
  onSend: (payload: { text?: string; file?: File }) => void;
};

export const FeaturesSupportChatMessageComposer = ({ disabled, sending, onSend }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = (): void => {
    setText('');
    setFile(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    if (picked.size > MAX_FILE_BYTES) {
      toast.error(t('support.chat.fileTooLarge'));
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setFile(picked);
  };

  const handleSubmit = (): void => {
    const trimmed = text.trim();
    if (!trimmed && !file) return;
    onSend({ text: trimmed || undefined, file: file ?? undefined });
    reset();
  };

  return (
    <div className="border-t border-slate-200 bg-white p-3">
      {file && (
        <div className="mb-2 flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-1.5 text-sm text-ink-soft">
          <Paperclip className="h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1 truncate">{file.name}</span>
          <button
            type="button"
            onClick={() => {
              setFile(null);
              if (inputRef.current) inputRef.current.value = '';
            }}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full text-ink-muted hover:bg-slate-200"
            aria-label={t('common.delete')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
          onChange={handlePick}
        />
        <IconButton
          label={t('support.chat.attach')}
          variant="ghost"
          size="md"
          disabled={disabled || sending}
          onClick={() => inputRef.current?.click()}
        >
          <Paperclip className="h-5 w-5" />
        </IconButton>
        <textarea
          rows={1}
          value={text}
          disabled={disabled || sending}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder={t('support.chat.messagePlaceholder')}
          className={cn(
            'max-h-32 min-h-[40px] flex-1 resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-ink shadow-sm outline-none placeholder:text-ink-faint',
            'focus:border-brand-500 focus:ring-2 focus:ring-brand-200',
            'disabled:cursor-not-allowed disabled:opacity-60',
          )}
        />
        <Button
          size="md"
          loading={sending}
          disabled={disabled || (!text.trim() && !file)}
          onClick={handleSubmit}
          leftIcon={<Send className="h-4 w-4" />}
        >
          {t('support.chat.send')}
        </Button>
      </div>
    </div>
  );
};
